import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, LinearProgress, Paper,
  FormControl, InputLabel, Select, MenuItem, Divider, Alert, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { People, School, AttachMoney, TrendingUp, Warning, CheckCircle } from '@mui/icons-material';
import dayjs from 'dayjs';
import { api } from '../api';

const TIME_RANGES = [
  { label: '30d', days: 30 },
  { label: '60d', days: 60 },
  { label: '90d', days: 90 },
  { label: '6m', days: 180 },
  { label: '12m', days: 365 },
  { label: 'All', days: null },
];

function StatCard({ icon, label, value, color, subtitle }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ textAlign: 'center', py: 2 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: '50%', mx: 'auto', mb: 1,
          bgcolor: `${color}.50`, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          {React.cloneElement(icon, { sx: { color: `${color}.main`, fontSize: 24 } })}
        </Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h5" fontWeight={700}>{value}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </CardContent>
    </Card>
  );
}

export default function ReportsPage() {
  const [dashboard, setDashboard] = useState(null);
  const [balances, setBalances] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [studentReport, setStudentReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashData, balData] = await Promise.all([
          api.getDashboard(),
          api.getBalances()
        ]);
        setDashboard(dashData);
        setBalances(balData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!selectedStudent) {
      setStudentReport(null);
      return;
    }
    api.getStudentReport(selectedStudent).then(setStudentReport).catch(console.error);
  }, [selectedStudent]);

  if (loading) return <Box sx={{ p: 2 }}><LinearProgress /></Box>;
  if (!dashboard) return <Box sx={{ p: 2 }}><Alert severity="error">Failed to load data</Alert></Box>;

  const studentsOwing = balances.filter(b => b.balance_owed > 0);
  const studentsPaidUp = balances.filter(b => b.balance_owed <= 0 && b.total_charges > 0);

  return (
    <Box sx={{ p: 2, maxWidth: 700, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Reports & Dashboard</Typography>

      {/* Stats Grid */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<People />} label="Students" value={dashboard.total_students} color="primary" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<School />} label="Classes" value={dashboard.total_classes} color="secondary" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<AttachMoney />} label="Total Charged" value={`$${dashboard.total_charges.toFixed(0)}`} color="info" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            icon={<TrendingUp />} label="Outstanding"
            value={`$${dashboard.total_outstanding.toFixed(0)}`}
            color={dashboard.total_outstanding > 0 ? 'error' : 'success'}
          />
        </Grid>
      </Grid>

      {/* Collection Progress */}
      {dashboard.total_charges > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">Collection Progress</Typography>
              <Typography variant="subtitle2" color="primary.main">
                {Math.min((dashboard.total_paid / dashboard.total_charges) * 100, 100).toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min((dashboard.total_paid / dashboard.total_charges) * 100, 100)}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Collected: ${dashboard.total_paid.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total: ${dashboard.total_charges.toFixed(2)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Students Owing Money */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        <Warning sx={{ fontSize: 20, mr: 0.5, color: 'warning.main', verticalAlign: 'text-bottom' }} />
        Students with Outstanding Balance ({studentsOwing.length})
      </Typography>
      {studentsOwing.length === 0 ? (
        <Card variant="outlined" sx={{ mb: 3, textAlign: 'center', py: 2 }}>
          <CheckCircle sx={{ color: 'success.main', fontSize: 32 }} />
          <Typography color="success.main" fontWeight={600}>All caught up!</Typography>
        </Card>
      ) : (
        <Card sx={{ mb: 3 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell align="right">Charged</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Owed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {studentsOwing
                  .sort((a, b) => b.balance_owed - a.balance_owed)
                  .map(student => (
                    <TableRow key={student.id} hover sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/students/${student.id}`)}>
                      <TableCell>
                        <Typography fontWeight={500}>{student.name}</Typography>
                      </TableCell>
                      <TableCell align="right">${student.total_charges.toFixed(2)}</TableCell>
                      <TableCell align="right">${student.total_paid.toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`$${student.balance_owed.toFixed(2)}`}
                          size="small" color="error"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Paid Up Students */}
      {studentsPaidUp.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>
            <CheckCircle sx={{ fontSize: 20, mr: 0.5, color: 'success.main', verticalAlign: 'text-bottom' }} />
            Paid Up Students ({studentsPaidUp.length})
          </Typography>
          <Card sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 2 }}>
              {studentsPaidUp.map(s => (
                <Chip key={s.id} label={s.name} color="success" variant="outlined"
                  onClick={() => navigate(`/students/${s.id}`)} clickable />
              ))}
            </Box>
          </Card>
        </>
      )}

      {/* Detailed Student Report */}
      <Divider sx={{ my: 2 }} />
      <Typography variant="h6" sx={{ mb: 1 }}>Detailed Student Report</Typography>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Select Student</InputLabel>
        <Select
          value={selectedStudent}
          onChange={e => setSelectedStudent(e.target.value)}
          label="Select Student"
        >
          <MenuItem value="">-- Select --</MenuItem>
          {balances.map(s => (
            <MenuItem key={s.id} value={s.id}>
              {s.name} {s.balance_owed > 0 ? `(owes $${s.balance_owed.toFixed(2)})` : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <StudentReportSection report={studentReport} />
    </Box>
  );
}

function StudentReportSection({ report }) {
  const [range, setRange] = useState('All');

  const cutoff = useMemo(() => {
    const r = TIME_RANGES.find(t => t.label === range);
    return r?.days ? dayjs().subtract(r.days, 'day').format('YYYY-MM-DD') : null;
  }, [range]);

  const filteredAttendance = useMemo(() => {
    if (!report) return [];
    const records = report.attendance_records || [];
    return cutoff ? records.filter(r => r.session_date >= cutoff) : records;
  }, [report, cutoff]);

  const filteredPayments = useMemo(() => {
    if (!report) return [];
    const payments = report.payments || [];
    return cutoff ? payments.filter(p => p.payment_date >= cutoff) : payments;
  }, [report, cutoff]);

  const filteredCharges = filteredAttendance.reduce((sum, a) => sum + (a.charge_amount || 0), 0);
  const filteredPaid = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

  if (!report) return null;

  return (
    <Card>
      <CardContent>
        {/* Timeframe Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <ToggleButtonGroup
            value={range} exclusive size="small"
            onChange={(_, v) => { if (v) setRange(v); }}
          >
            {TIME_RANGES.map(t => (
              <ToggleButton key={t.label} value={t.label} sx={{ px: 1.5, textTransform: 'none' }}>
                {t.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* Summary */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Classes Attended</Typography>
            <Typography variant="h6" fontWeight={700}>{filteredAttendance.length}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Charged</Typography>
            <Typography variant="h6">${filteredCharges.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Paid</Typography>
            <Typography variant="h6">${filteredPaid.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Balance</Typography>
            {(() => {
              const filteredBalance = filteredCharges - filteredPaid;
              return (
                <Typography variant="h6" color={filteredBalance > 0 ? 'error.main' : filteredBalance < 0 ? 'success.main' : 'text.primary'}>
                  ${Math.abs(filteredBalance).toFixed(2)}
                  {filteredBalance < 0 && ' (credit)'}
                </Typography>
              );
            })()}
          </Box>
        </Box>

        {/* Unpaid Sessions */}
        {report.unpaid_dates.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Unpaid Sessions</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Class</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.unpaid_dates.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>{item.date}</TableCell>
                      <TableCell>{item.class_name}</TableCell>
                      <TableCell align="right">${item.charge.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Attendance History */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Attendance History ({filteredAttendance.length} sessions)</Typography>
        {filteredAttendance.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No attendance in this period</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell align="right">Charge</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAttendance.map((a, i) => (
                  <TableRow key={i}>
                    <TableCell>{a.session_date}</TableCell>
                    <TableCell>{a.class_name}</TableCell>
                    <TableCell align="right">${(a.charge_amount || 0).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Payment History */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Payment History ({filteredPayments.length} transactions)</Typography>
        {filteredPayments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No payments in this period</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPayments.map((p, i) => (
                  <TableRow key={i}>
                    <TableCell>{p.payment_date}</TableCell>
                    <TableCell>{p.method || '-'}</TableCell>
                    <TableCell align="right">${p.amount.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
